import { yupResolver } from '@hookform/resolvers/yup';
import { Tab } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  EstadosActivacionInstallOTEnumChoice,
  a11yProps,
  activacionInstallOTChangePortSchema,
  useTabsFormOnly,
} from '@/shared';
import {
  SaveChangePortInstallOTData,
  useUpdateOrdenTrabajo,
} from '@/shared/actions/erp';
import {
  CustomTabPanel,
  FormTabsOnly,
  TabsFormBoxScene,
} from '@/shared/components/ui';
import { AlertSeverityEnum, OrdenTrabajo } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { cambiosPendientesOTActivacionUrl } from '../../../pages';
import {
  ActivacionInstaOTActiDataFormTab,
  ActivacionInstallOTClientDataFormTab,
} from '../form';

export type SaveActivacionCambioPuertoInstallOTProps = {
  ordenTrabajo: OrdenTrabajo;
  titlePageNode?: React.ReactNode;
  titlePage?: string;

  customReturnUrl?: string;
};

type SaveActivacionChangePortData = any & {};

const SaveActivacionCambioPuertoInstallOT: React.FC<
  SaveActivacionCambioPuertoInstallOTProps
> = ({
  ordenTrabajo,
  titlePage,
  customReturnUrl = cambiosPendientesOTActivacionUrl,
  titlePageNode = null,
}) => {
    ///* hooks ------------------------
    const navigate = useNavigate();
    const { handleMethodChange, valueTab } = useTabsFormOnly();

    ///* global state ------------------------
    const setAlert = useUiStore(s => s.setAlert);

    ///* form ------------------------
    const form = useForm<SaveActivacionChangePortData>({
      resolver: yupResolver(activacionInstallOTChangePortSchema) as any,
    });

    ///* mutations ------------------------
    const saveNewPortOT = useUpdateOrdenTrabajo<SaveChangePortInstallOTData>({
      navigate,
      returnUrl: customReturnUrl,
    });

    ///* handlers ------------------------
    const onSave = async (data: SaveActivacionChangePortData) => {
      saveNewPortOT.mutate({
        id: ordenTrabajo.id_orden_trabajo!,
        data: {
          estado_activacion: EstadosActivacionInstallOTEnumChoice.GESTIONADA,
          puerto: data.puerto!,
          distancia: data.distancia!,
          nap: data.nap!,
          nodo: data.nodo,
          olt: data.olt,
          router: data.router,
        },
      });
    };

    ///* effects ------------------------
    useEffect(() => {
      if (!ordenTrabajo?.id_orden_trabajo) return;

      const { prospecto_data, preventa_data, agendamiento_data, ...rest } =
        ordenTrabajo;

      ///* reset form -------------
      form.reset({
        ...prospecto_data,
        ...preventa_data,
        ...agendamiento_data,
        ...rest,
        planLabel: ordenTrabajo?.plan_data?.nombre || '',
        flotaLabel: ordenTrabajo?.flota_data?.nombre || '',
        tipo_instalacion: preventa_data?.tipo_instalacion || '',
        valor_instalacion: preventa_data?.valor_instalacion || '',

        observacion_1: ordenTrabajo?.preventa_data?.observaciones?.observacion_1,
        observacion_2: ordenTrabajo?.preventa_data?.observaciones?.observacion_2,
        observacion_llamada:
          agendamiento_data?.observaciones?.observacion_llamada,

        condicion_residencia: preventa_data?.condicion_residencia || '',

        numero_documento: rest.contrato_data?.numero_referencia,

        velocidad_internet: preventa_data?.velocidad
          ? `${preventa_data?.velocidad} ${rest?.plan_data?.unidad}`
          : '',

        //
        ciudad: rest.ciudad_data?.nombre,
        zona: rest.zona_data?.nombre,
        sector: rest.sector_data?.nombre,
        rawNap: rest.nap_data,
        distancia: agendamiento_data?.distancia || '0',

        // check this
        puerto: '',

        observacion_activacion: rest?.observaciones?.observacion_activacion,
        coordenadas: rest?.coordenadas,
        /* } as unknown as SaveFormDataActivacionInstallPendingOT); */
      } as unknown);

      ///* set equipos disponibles -------------
    }, [ordenTrabajo, form.reset]);

    return (
      <TabsFormBoxScene
        titlePage={titlePage!}
        titlePageNode={titlePageNode}
        onCancel={() => {
          navigate(customReturnUrl);
        }}
        saveTextBtn="Actualizar"
        onSave={form.handleSubmit(onSave, err => {
          console.log('err', err);
          setAlert(
            true,
            'Debe completar los campos requeridos.',
            AlertSeverityEnum.error
          );
        })}
        tabs={
          <FormTabsOnly onChange={handleMethodChange} value={valueTab}>
            <Tab label="DATOS GENERALES CLIENTE" value={1} {...a11yProps(1)} />
            <Tab label="DETALLE ACTIVACIÓN" value={2} {...a11yProps(2)} />
          </FormTabsOnly>
        }
      >
        {/* ============ DATOS GENERALES CLIENTE ============ */}
        <CustomTabPanel index={valueTab} value={1}>
          <ActivacionInstallOTClientDataFormTab form={form} />
        </CustomTabPanel>

        {/* ============ DETALLE ACTIVACIÓN ============ */}
        <CustomTabPanel index={valueTab} value={2}>
          <ActivacionInstaOTActiDataFormTab
            form={form}
            ordenTrabajo={ordenTrabajo}
            isUpdatingPort
          />
        </CustomTabPanel>
      </TabsFormBoxScene>
    );
  };

export default SaveActivacionCambioPuertoInstallOT;
